import { useEffect, useMemo } from "react"
import { useRequestData } from "./useRequestData"
import { analyticsWrap } from "src/tags/analytics"
import { crashlyticsWrap } from "src/tags/crashlytics"
import { registerDevice } from "src/apis/auth/createDevice"
import { getUpdateDeviceFlag, setUpdateDeviceFlag } from "src/storage/updateDeviceFlag"
import { useSetRecoilState } from "recoil"
import { deviceTokenState } from "src/recoil/atoms/account/deviceTokenState"
import * as Notifications from "expo-notifications"
import { Platform } from "react-native"

export const useAccountIdEffect = () => {
  const requestDataResult = useRequestData()
  const setDeviceToken = useSetRecoilState(deviceTokenState)
  const content = useMemo(() => {
    if (!requestDataResult.isOk) {
      return
    }
    return requestDataResult.content
  }, [requestDataResult])

  useEffect(() => {
    let isMounted = true
    let retryTimer: NodeJS.Timeout | null = null

    const cleanup = () => {
      isMounted = false
      if (retryTimer) {
        clearTimeout(retryTimer)
        retryTimer = null
      }
    }

    ;(async () => {
      if (content?.accountId) {
        if (content?.accessToken.length > 0) {
          getUpdateDeviceFlag().then(async (value) => {
            if (!value) {
              const result = await registerDevice(content?.accessToken)

              if (result.isOk) {
                setUpdateDeviceFlag()

                if (Platform.OS === "ios" || Platform.OS === "android") {
                  try {
                    const token = await Notifications.getDevicePushTokenAsync()
                    if (token?.data) {
                      setDeviceToken(token.data)
                    }
                  } catch (e) {
                    console.error("[useAccountIdEffect] Failed to store device token:", e)
                  }
                }
              } else {
                if (
                  !result.isOk &&
                  (result.error?.message?.includes("network") || result.error?.message?.includes("timeout"))
                ) {
                  retryTimer = setTimeout(async () => {
                    if (!isMounted) return

                    const retryResult = await registerDevice(content?.accessToken)
                    if (retryResult.isOk && isMounted) {
                      setUpdateDeviceFlag()

                      if (Platform.OS === "ios" || Platform.OS === "android") {
                        try {
                          const token = await Notifications.getDevicePushTokenAsync()
                          if (token?.data && isMounted) {
                            setDeviceToken(token.data)
                          }
                        } catch (e) {
                          console.error("[useAccountIdEffect] Failed to store device token on retry:", e)
                        }
                      }
                    }
                  }, 10000)
                }
              }
            } else {
              if (Platform.OS !== "web") {
                try {
                  const token = await Notifications.getDevicePushTokenAsync()
                  if (token?.data) {
                    setDeviceToken(token.data)
                  }
                } catch (e) {
                  console.error("[useAccountIdEffect] Failed to sync existing token:", e)
                }
              }
            }
          })
        }

        try {
          analyticsWrap.setUser(content?.accountId)
          crashlyticsWrap.setUser(content?.accountId)
        } catch (e) {
          crashlyticsWrap.recordError(e, "accountIdSetError")
        }
      }
    })()

    return cleanup
  }, [content, setDeviceToken])
}
